import { supabaseAuthStorageKey } from './supabase-auth-storage-key.ts';

const PERSIST_AUTH_KEY = 'persist_auth';
const MAX_PERSISTED_SESSION_BYTES = 100_000;

type AuthStorageReader = Pick<Storage, 'getItem'>;

function browserStorage(name: 'localStorage' | 'sessionStorage'): AuthStorageReader | null {
  if (typeof window === 'undefined') return null;
  try {
    return window[name];
  } catch {
    return null;
  }
}

function storedValue(storage: AuthStorageReader | null, key: string): string | null {
  try {
    return storage?.getItem(key) || null;
  } catch {
    return null;
  }
}

function jwtPayload(accessToken: string): Record<string, unknown> | null {
  const encoded = accessToken.split('.')[1];
  if (!encoded) return null;
  try {
    const base64 = encoded.replaceAll('-', '+').replaceAll('_', '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const payload = JSON.parse(globalThis.atob(padded));
    return payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : null;
  } catch {
    return null;
  }
}

export function readPersistedAuthenticatedUserId({
  localStorage = browserStorage('localStorage'),
  now = Date.now(),
  sessionStorage = browserStorage('sessionStorage'),
  storageKey = supabaseAuthStorageKey(import.meta.env.VITE_SUPABASE_URL || ''),
}: {
  localStorage?: AuthStorageReader | null;
  now?: number;
  sessionStorage?: AuthStorageReader | null;
  storageKey?: string;
} = {}): string | null {
  const persistent = storedValue(localStorage, PERSIST_AUTH_KEY) === 'true';
  const serialized = persistent
    ? storedValue(localStorage, storageKey) || storedValue(sessionStorage, storageKey)
    : storedValue(sessionStorage, storageKey);
  if (!serialized || serialized.length > MAX_PERSISTED_SESSION_BYTES) return null;

  try {
    const session = JSON.parse(serialized) as Record<string, unknown>;
    const user = session.user;
    if (!user || typeof user !== 'object' || Array.isArray(user)) return null;
    const userId = (user as Record<string, unknown>).id;
    const accessToken = session.access_token;
    const expiresAt = session.expires_at;
    if (
      typeof userId !== 'string' ||
      !userId ||
      typeof accessToken !== 'string' ||
      !accessToken ||
      typeof expiresAt !== 'number' ||
      !Number.isFinite(expiresAt) ||
      expiresAt <= Math.floor(now / 1000)
    ) {
      return null;
    }

    const claims = jwtPayload(accessToken);
    if (
      !claims ||
      claims.sub !== userId ||
      typeof claims.exp !== 'number' ||
      !Number.isFinite(claims.exp) ||
      claims.exp <= Math.floor(now / 1000) ||
      claims.exp !== expiresAt
    ) {
      return null;
    }
    return userId;
  } catch {
    return null;
  }
}
